import React, { useEffect, useState } from "react";
import {
  Button,
  FormGroup,
  Label,
  Input,
  Form,
  Spinner,
  Table,
  Card,
  CardHeader,
  CardBody,
} from "reactstrap";
import { useParams } from "react-router-dom";

// Define types for the form values
interface Question {
  question: string;
  answer: string; // Yes, No, or NA
  sort_id: number;
  equipment_name: string;
  equipment_used_questionnaire: string;
}

interface FormValues {
  questions: Question[];
}

interface EquipmentUsedQuestionnaireProps {
  previousStep: () => void;
  nextStep:()=> void;
  formik: any;
  permitData: any;
  refetchPermit: any;
  questionnaireData: any;
  setInitialQuestions: any;
  patchisLoading: boolean;
  questionLoading: boolean;
  isPermitLoading: boolean;
}

const EquipmentUsedQuestionnaire: React.FC<EquipmentUsedQuestionnaireProps> = ({
  previousStep,
  nextStep,
  formik,
  permitData,
  refetchPermit,
  questionnaireData,
  setInitialQuestions,
  patchisLoading,
  questionLoading,
  isPermitLoading,
}) => {
  const { hwPermitNo } = useParams<{ hwPermitNo: string }>();

  const groupQuestionsByEquipment = (questions: Question[]): Record<string, Question[]> => {
    const groups: Record<string, Question[]> = {};
    
    questions?.forEach((question) => {
      const key = question.equipment_name || 'General Questions';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(question);
    });

    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => (a.sort_id || 0) - (b.sort_id || 0));
    });

    return groups;
  };

  useEffect(() => {
    if (questionnaireData) {
      const initialQuestions = questionnaireData
        .map((q: any) => ({
          equipment_used_questionnaire: q.id,
          question: q.question,
          answer: permitData?.equipment_used?.find((eq: any) => 
            eq.equipment_used_questionnaire === q.id
          )?.answer || "",
          sort_id: q.sort_id || 0,
          equipment_name: q.equipment_name || 'General Questions',
        }))
        .sort((a: Question, b: Question) => (a.sort_id || 0) - (b.sort_id || 0));

      setInitialQuestions(initialQuestions);
    } else {
      setInitialQuestions([]);
    }
  }, [questionnaireData, permitData]);

  const handlePreviousStep = () => {
    refetchPermit();
    previousStep();
  };

  const handleNext = () => {
    if (!questionnaireData || questionnaireData.length === 0) {
      nextStep(); // Skip submit if no questions
    } else {
      formik.handleSubmit(); // Submit form if questions exist
    }
  };

  if (questionLoading || isPermitLoading) {
    return <Spinner color="primary" />;
  }

  const groupedQuestions = groupQuestionsByEquipment(formik.values.questions || []);

  return (
    <>
      {Object.keys(groupedQuestions).length > 0 ? (
          <Form onSubmit={formik.handleSubmit}>
          {Object.entries(groupedQuestions).map(([equipmentName, questions]) => (
            <Card key={equipmentName} className="mb-4">
            <CardHeader>
              <h5>{equipmentName}</h5>
            </CardHeader>
            <CardBody>
              <Table className="table table-striped">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Answer</th>
                  </tr>
                </thead>
                <tbody>
                  {questions?.map((q, index) => {
                    const originalIndex = formik.values.questions.findIndex(
                      (question: Question) => 
                        question.equipment_used_questionnaire === q.equipment_used_questionnaire
                    );

                    return (
                      <tr key={`${q.equipment_used_questionnaire}`}>
                        <td width={'70%'}>{q.question}</td>
                        <td width={'30%'}>
                          <FormGroup check inline>
                            <Label check className="mr-2">
                              <Input
                                type="radio"
                                name={`questions[${originalIndex}].answer`}
                                value="Yes"
                                checked={formik.values.questions[originalIndex]?.answer === "Yes"}
                                onChange={formik.handleChange}
                              />{" "}
                              Yes
                            </Label>
                          </FormGroup>
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="radio"
                                name={`questions[${originalIndex}].answer`}
                                value="No"
                                checked={formik.values.questions[originalIndex]?.answer === "No"}
                                onChange={formik.handleChange}
                              />{" "}
                              No
                            </Label>
                          </FormGroup>
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="radio"
                                name={`questions[${originalIndex}].answer`}
                                value="NA"
                                checked={formik.values.questions[originalIndex]?.answer === "NA"}
                                onChange={formik.handleChange}
                              />{" "}
                              N/A
                            </Label>
                          </FormGroup>
                          {formik.touched.questions?.[originalIndex]?.answer && (
                            <div className="text-danger">
                              {formik.errors.questions?.[originalIndex]?.answer}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </CardBody>
          </Card>
          ))}
        </Form>
      ) : (
        <div className="mb-4">No questions available for this permit</div>
      )}

      <div className="d-flex justify-content-between">
        <Button
          color="secondary"
          onClick={handlePreviousStep}
          disabled={patchisLoading || questionLoading}
        >
          Previous
        </Button>
        <Button
          color="primary"
          disabled={patchisLoading || questionLoading}
          onClick={handleNext} // Use handleNext instead of type="submit"
        >
          {patchisLoading ? <Spinner size="sm" /> : "Next"}
        </Button>
      </div>
    </>
  );
};

export default EquipmentUsedQuestionnaire;